import React, { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  Typography,
  styled,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  resolveFacilityTypeSlugForHistoricPenalties,
  shouldShowFacilityHistoricPenaltiesSection,
  type DiscoverSomNavigationState,
  type FacilityIndividualDashboardResponse,
} from "@services/facilityDashboard";
import DashboardSparkline from "./dashboard/DashboardSparkline";
import DashboardComplianceCalendarStrip from "./dashboard/DashboardComplianceCalendarStrip";
import HistoricPenaltiesDashboardCard from "./dashboard/HistoricPenaltiesDashboardCard";
import { CmsNursingHomePdcInfoTooltip } from "./dashboard/dashboardInfoTooltips";
import {
  DashboardMetricActionButton,
  DashboardSectionHeader,
  DashboardTextActionLink,
} from "./dashboard/DashboardSectionHeader";
import {
  DASHBOARD,
  dashboardAccordionSx,
  dashboardAccordionSummarySx,
  dashboardAccordionSummaryRootProps,
  dashboardBadgeSx,
  DASHBOARD_CARD_GAP,
  dashboardImpactChipSx,
  dashboardListItemSx,
  DOCUMENT_EXPIRY_LIST_MAX_HEIGHT,
  DOCUMENT_EXPIRY_SCROLL_THRESHOLD,
  getDashboardTintedCardStyles,
  getDashboardWhiteCardStyles,
} from "./dashboard/dashboardTheme";
import {
  DocumentExpiryInfoTooltip,
  FacilityProfileCompletenessInfoTooltip,
  PenaltyExposureInfoTooltip,
  RegulationImpactInfoTooltip,
} from "./dashboard/dashboardInfoTooltips";
import type { ComplianceCalendarItem, PenaltyItem } from "@services/facilityCompliance";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import { isGlobalAdministrator } from "@utils/userUtils";
import {
  buildComplianceCalendarCountsByDate,
  buildComplianceCalendarEventsByDate,
} from "./dashboard/dashboardComplianceCalendarUtils";
import { DashboardTileMetricPlaceholder } from "./dashboard/DashboardTileMetricPlaceholder";
import {
  canShowPenaltyExposureProgressDuringTileLoad,
  isPenaltyExposureDashboardReady,
  isPenaltyExposureTilePending,
  resolvePenaltyExposureDisplayLabel,
  resolvePenaltyExposureFromPenaltiesData,
  shouldShowPenaltyExposureProgressList,
} from "./dashboard/dashboardPenaltyExposure";
import { PenaltyExposureProgressList } from "./dashboard/PenaltyExposureProgressList";
import {
  allPenaltyExposureProgressStepsComplete,
  type PenaltyExposureProgressStep,
} from "./dashboard/penaltyExposureProgressSteps";

/** Re-enable when the regulation impact accordion is ready for release. */
const REGULATION_IMPACT_SECTION_ENABLED = false;

const TileCard = styled(Box)(({ theme }) => ({
  ...getDashboardTintedCardStyles(theme),
  flex: 1,
  minWidth: 200,
  cursor: "pointer",
  transition: "border-color 0.2s ease",
  "&:hover": { borderColor: "#9dd4dc" },
}));

const SectionCard = styled(Box)(({ theme }) => getDashboardWhiteCardStyles(theme));

const TintedSectionCard = styled(Box)(({ theme }) => getDashboardTintedCardStyles(theme));

export type FacilityDashboardNavigateTarget =
  | "penalty"
  | "facilities"
  | "facilityProfile"
  | "discover"
  | "compliance"
  | "riskanalysis";

export type FacilityDashboardNavigateOptions =
  | DiscoverSomNavigationState
  | { section: "policy" }
  | { section: "incident" | "survey" };

export interface FacilityDashboardTabProps {
  facility: MyFacilityListRow;
  onNavigate: (
    target: FacilityDashboardNavigateTarget,
    options?: FacilityDashboardNavigateOptions,
  ) => void;
  /** From facility-compliance/parallel in MyFacilityDetails (session state). */
  complianceCalendar?: ComplianceCalendarItem[] | null;
  /** Potential Penalties accordion rows (penaltiesAndTotalExposure.items). */
  potentialPenaltyItems?: PenaltyItem[] | null;
  /**
   * @deprecated Dashboard penalty tile loads from the dashboard BFF only; ignored for loading state.
   */
  penaltyExposureAwaitingCompliance?: boolean;
  /**
   * @deprecated Use `penaltyExposureAwaitingCompliance` (kept for callers not yet updated).
   */
  complianceCalendarLoading?: boolean;
  /** Effective as-of date for quarter windows (matches Potential Penalties tab). */
  asOfDate?: string | null;
  /** Dashboard BFF payload (loaded by MyFacilityDetails). */
  data?: FacilityIndividualDashboardResponse | null;
  /** True while the dashboard BFF request is in flight. */
  loading?: boolean;
  /** Set when the dashboard BFF request failed. */
  error?: string | null;
  /** Retry the dashboard BFF request. */
  onRetry?: () => void;
  /** When false, hide loading UI until the tab is shown again. */
  isTabActive?: boolean;
  /** Live checklist rows for penalty exposure calculation (dashboard SSE progress). */
  penaltyExposureProgressSteps?: PenaltyExposureProgressStep[];
  /**
   * Network child dashboard — hide open-in-new / tile redirects (stay in Network shell).
   */
  hideExternalNavigation?: boolean;
}

function formatLastRunLabel(isoOrNull: string | null | undefined): string {
  if (!isoOrNull?.trim()) return "Never";
  const d = new Date(isoOrNull);
  if (Number.isNaN(d.getTime())) return "Never";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const ANALYSIS_TYPE_LINKS: {
  label: string;
  target: FacilityDashboardNavigateTarget;
  section: "policy" | "incident" | "survey";
}[] = [
  { label: "Policy", target: "compliance", section: "policy" },
  { label: "Incident", target: "riskanalysis", section: "incident" },
  { label: "Survey", target: "riskanalysis", section: "survey" },
];

function build30DayGridBounds(reference: Date): { start: Date; end: Date } {
  const start = new Date(reference);
  start.setHours(0, 0, 0, 0);
  const dow = start.getDay();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  start.setDate(start.getDate() + mondayOffset);
  const end = new Date(start);
  end.setDate(start.getDate() + 34);
  return { start, end };
}

export default function FacilityDashboardTab({
  facility,
  onNavigate,
  complianceCalendar,
  potentialPenaltyItems,
  complianceCalendarLoading = false,
  asOfDate: asOfDateProp,
  data = null,
  loading = false,
  error = null,
  onRetry,
  isTabActive = true,
  penaltyExposureProgressSteps = [],
  hideExternalNavigation = false,
}: FacilityDashboardTabProps) {

  const { complianceEventCountsByDate, complianceEventsByDate } = useMemo(() => {
    const { start, end } = build30DayGridBounds(new Date());
    return {
      complianceEventCountsByDate: buildComplianceCalendarCountsByDate(
        complianceCalendar,
        start,
        end,
      ),
      complianceEventsByDate: buildComplianceCalendarEventsByDate(
        complianceCalendar,
        start,
        end,
      ),
    };
  }, [complianceCalendar]);

  const facilityPenalties = data?.historic_penalties_facility;
  const facilityTypeSlugForHistoricPenalties = useMemo(
    () =>
      resolveFacilityTypeSlugForHistoricPenalties(
        facility.facility_type_slug,
        facility.facility_type,
      ),
    [facility.facility_type_slug, facility.facility_type],
  );
  const showFacilityHistoricPenalties = useMemo(
    () =>
      shouldShowFacilityHistoricPenaltiesSection(
        facility,
        facilityTypeSlugForHistoricPenalties,
        facilityPenalties,
      ),
    [facility, facilityTypeSlugForHistoricPenalties, facilityPenalties],
  );
  const pdcMeta = data?.cms_nursing_home_pdc;

  const dashboardTilesLoading = loading || (isTabActive && !data && !error);
  const asOfDate = asOfDateProp?.trim() || new Date().toISOString().slice(0, 10);
  const apiPenaltyTile = data?.tiles.penalty_exposure;
  // Penalty exposure comes from the dashboard BFF (Redis compliance cache when warm).
  // Do not block the tile on facility-compliance/parallel — that LLM path can take minutes.
  const penaltyTileLoading = dashboardTilesLoading;
  const penaltiesDataReady =
    (complianceCalendar?.length ?? 0) > 0 || (potentialPenaltyItems?.length ?? 0) > 0;
  const progressPipelineComplete = allPenaltyExposureProgressStepsComplete(
    penaltyExposureProgressSteps,
  );
  const exposure =
    apiPenaltyTile && !penaltyTileLoading
      ? resolvePenaltyExposureFromPenaltiesData(
          apiPenaltyTile,
          complianceCalendar,
          potentialPenaltyItems,
          asOfDate,
        )
      : null;
  const penaltyExposurePending =
    !penaltyTileLoading &&
    exposure != null &&
    isPenaltyExposureTilePending(exposure, {
      awaitingComplianceCalendar: complianceCalendarLoading && !penaltiesDataReady,
    });
  const penaltyExposureCalculated = isPenaltyExposureDashboardReady(exposure, {
    penaltyExposurePending,
    penaltiesDataReady,
    complianceCalendarLoading,
    progressPipelineComplete,
  });
  const penaltyDisplayLabel = resolvePenaltyExposureDisplayLabel(
    exposure,
    penaltyExposureCalculated,
  );
  const isGlobalAdmin = isGlobalAdministrator();
  const showPenaltyProgress = shouldShowPenaltyExposureProgressList(
    penaltyExposureProgressSteps,
    penaltyExposureCalculated,
    complianceCalendarLoading,
  );
  const showPenaltyProgressDuringTileLoad = canShowPenaltyExposureProgressDuringTileLoad(
    penaltyExposureProgressSteps,
    penaltyExposureCalculated,
    penaltyTileLoading,
    complianceCalendarLoading,
  );

  if (error && !data) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {error ?? "No data"}
        </Typography>
        <Link component="button" type="button" onClick={() => onRetry?.()} sx={{ color: DASHBOARD.accent }}>
          Retry
        </Link>
      </Box>
    );
  }

  const showPenaltyTrend =
    penaltyExposureCalculated &&
    exposure?.has_quarter_trend === true &&
    (exposure.quarter_delta ?? 0) !== 0;
  const regUpdates = data?.tiles.regulation_updates;
  const analyses = data?.tiles.analyses_run;

  const regulationUpdatesSubtitle = (() => {
    if (!regUpdates) {
      return null;
    }
    if (regUpdates.subtitle) {
      return regUpdates.subtitle;
    }
    const parts: string[] = [];
    if (regUpdates.facility_type_label) {
      parts.push(regUpdates.facility_type_label);
    }
    if (regUpdates.som_appendix_letter) {
      parts.push(`Appendix ${regUpdates.som_appendix_letter}`);
    }
    return parts.length > 0 ? parts.join(" · ") : null;
  })();

  return (
    <Box
      sx={{
        fontFamily: "Rethink Sans, sans-serif",
        pb: 4,
        bgcolor: DASHBOARD.pageBg,
        px: { xs: 0, sm: 0.5 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: DASHBOARD_CARD_GAP,
          mb: DASHBOARD_CARD_GAP,
        }}
      >
        <TileCard
          onClick={
            hideExternalNavigation || penaltyTileLoading
              ? undefined
              : () => onNavigate("penalty")
          }
          role={
            hideExternalNavigation || penaltyTileLoading ? undefined : "link"
          }
          sx={
            hideExternalNavigation || penaltyTileLoading
              ? { cursor: "default", "&:hover": { borderColor: undefined } }
              : undefined
          }
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: DASHBOARD.header }}>
                Penalty Exposure
              </Typography>
              <PenaltyExposureInfoTooltip />
            </Box>
            {!hideExternalNavigation ? (
              <DashboardMetricActionButton
                onClick={penaltyTileLoading ? undefined : () => onNavigate("penalty")}
              />
            ) : null}
          </Box>
          {penaltyTileLoading && !showPenaltyProgressDuringTileLoad ? (
            <DashboardTileMetricPlaceholder minHeight={96} />
          ) : (
            <>
              {showPenaltyProgress ? (
                <PenaltyExposureProgressList
                  steps={penaltyExposureProgressSteps}
                  showStepList={isGlobalAdmin}
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: penaltyExposureCalculated ? 32 : 15,
                    fontWeight: penaltyExposureCalculated ? 700 : 600,
                    color: penaltyExposureCalculated ? DASHBOARD.metricAccent : DASHBOARD.text,
                    mt: 1,
                    lineHeight: 1.25,
                    fontStyle: penaltyExposureCalculated ? "normal" : "italic",
                  }}
                >
                  {penaltyDisplayLabel}
                </Typography>
              )}
              {showPenaltyTrend ? (
                <>
                  <Typography sx={{ fontSize: 13, color: DASHBOARD.text, mt: 0.5 }}>
                    {exposure.quarter_delta_direction === "up"
                      ? "↑"
                      : exposure.quarter_delta_direction === "down"
                        ? "↓"
                        : ""}{" "}
                    {exposure.quarter_delta_display} from last quarter
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <DashboardSparkline points={exposure.sparkline_points} />
                  </Box>
                </>
              ) : null}
              {penaltyExposureCalculated && exposure?.exposure_note ? (
                <Typography
                  data-testid="penalty-exposure-note"
                  sx={{ fontSize: 12, color: DASHBOARD.text, mt: 1, lineHeight: 1.4 }}
                >
                  {exposure.exposure_note}
                </Typography>
              ) : null}
            </>
          )}
        </TileCard>

        <TileCard
          onClick={
            hideExternalNavigation || dashboardTilesLoading
              ? undefined
              : () => onNavigate("discover", { category: "latest-updates" })
          }
          role={
            hideExternalNavigation || dashboardTilesLoading ? undefined : "link"
          }
          sx={
            hideExternalNavigation || dashboardTilesLoading
              ? { cursor: "default", "&:hover": { borderColor: undefined } }
              : undefined
          }
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: DASHBOARD.header }}>
              Regulation Updates
            </Typography>
            {!hideExternalNavigation ? (
              <DashboardMetricActionButton
                onClick={
                  dashboardTilesLoading
                    ? undefined
                    : () => onNavigate("discover", { category: "latest-updates" })
                }
              />
            ) : null}
          </Box>
          {dashboardTilesLoading || !regUpdates ? (
            <DashboardTileMetricPlaceholder minHeight={96} />
          ) : (
            <>
              <Typography sx={{ fontSize: 32, fontWeight: 700, color: DASHBOARD.metricAccent, mt: 1, lineHeight: 1.1 }}>
                {regUpdates.last_som_updated_display}
              </Typography>
              <Typography sx={{ fontSize: 13, color: DASHBOARD.text }}>
                {[
                  "SOM last updated",
                  regulationUpdatesSubtitle,
                  regUpdates.count_this_month > 0
                    ? `${regUpdates.count_this_month} ${regUpdates.count_this_month === 1 ? "tag" : "tags"}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(". ")}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap" }}>
                {(regUpdates.som_tags?.length
                  ? regUpdates.som_tags
                  : regUpdates.regulation_tags.map((tag) => ({ tag, section_id: null }))
                ).map((row) => (
                  <Chip
                    key={row.tag}
                    label={row.tag}
                    size="small"
                    clickable
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigate("discover", {
                        category: "latest-updates",
                        somTag: row.tag,
                        sectionId: row.section_id ?? undefined,
                      });
                    }}
                    sx={{
                      fontSize: 11,
                      bgcolor: DASHBOARD.listItemBg,
                      border: `1px solid ${DASHBOARD.borderSubtle}`,
                      color: DASHBOARD.metricAccent,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#e8f6f8" },
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </TileCard>

        <TileCard
          sx={{ cursor: "default", "&:hover": { borderColor: undefined } }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: DASHBOARD.header }}>
            Analyses Run
          </Typography>
          {dashboardTilesLoading || !analyses ? (
            <DashboardTileMetricPlaceholder minHeight={88} />
          ) : (
            <>
              <Typography sx={{ fontSize: 32, fontWeight: 700, color: DASHBOARD.metricAccent, mt: 1, lineHeight: 1.1 }}>
                {analyses.count_90d}
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: 13, color: DASHBOARD.text, display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 0.25 }}
              >
                Last 90 days
                {ANALYSIS_TYPE_LINKS.map((item) => (
                  <React.Fragment key={item.label}>
                    <span> · </span>
                    <Link
                      component="button"
                      type="button"
                      disabled={dashboardTilesLoading}
                      onClick={() => onNavigate(item.target, { section: item.section })}
                      sx={{
                        color: DASHBOARD.accent,
                        fontSize: 13,
                        fontWeight: 600,
                        verticalAlign: "baseline",
                        p: 0,
                        minWidth: 0,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {item.label}
                    </Link>
                  </React.Fragment>
                ))}
              </Typography>
              <Typography sx={{ fontSize: 12, color: DASHBOARD.textMuted, mt: 1 }}>
                Last run: {formatLastRunLabel(analyses.last_run_at)}
              </Typography>
            </>
          )}
        </TileCard>
      </Box>

      {dashboardTilesLoading || !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: DASHBOARD.accent }} />
        </Box>
      ) : (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: DASHBOARD_CARD_GAP,
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: DASHBOARD_CARD_GAP }}>
          <SectionCard>
            <DashboardSectionHeader
              title="Action required"
              subtitle="Items detected that need your awareness."
            />
            {data!.action_required.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: DASHBOARD.text, mt: 1 }}>
                No items requiring immediate attention
              </Typography>
            ) : (
              data!.action_required.map((item) => (
                <Accordion
                  key={item.id}
                  disableGutters
                  sx={{
                    ...dashboardAccordionSx,
                    border: `1px solid ${DASHBOARD.borderSubtle}`,
                    borderRadius: "8px",
                    mb: 1,
                    bgcolor: DASHBOARD.listItemBg,
                  }}
                >
                  <AccordionSummary
                    {...dashboardAccordionSummaryRootProps}
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ ...dashboardAccordionSummarySx, px: 2, py: 1.5 }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", pr: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14, color: DASHBOARD.header }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: DASHBOARD.textMuted }}>
                        {item.relative_timestamp}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                    <Typography sx={{ fontSize: 13, color: DASHBOARD.text, mb: 1 }}>
                      {item.description}
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      onClick={() =>
                        onNavigate(
                          item.feature_link === "facilityProfile"
                            ? "facilityProfile"
                            : item.feature_link === "penalty"
                              ? "penalty"
                              : "facilities",
                        )
                      }
                      sx={{ color: DASHBOARD.accent, fontSize: 14, fontWeight: 500 }}
                    >
                      Go to {item.feature_label} →
                    </Link>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </SectionCard>

          <DashboardComplianceCalendarStrip
            monthLabel={data!.calendar_strip.month_label}
            year={data!.calendar_strip.year}
            events={data!.calendar_strip.events}
            complianceEventCountsByDate={complianceEventCountsByDate}
            complianceEventsByDate={complianceEventsByDate}
            onViewFullCalendar={
              hideExternalNavigation ? undefined : () => onNavigate("penalty")
            }
            onDayClick={
              hideExternalNavigation ? undefined : () => onNavigate("penalty")
            }
          />

          {showFacilityHistoricPenalties ? (
            <HistoricPenaltiesDashboardCard
              title="Historic Penalties"
              subtitle="Last 5 years · Documented enforcement for your facility"
              penalties={
                facilityPenalties ?? {
                  has_data: false,
                  yearly_totals: [],
                  total_amount: 0,
                  total_violations: 0,
                  most_cited_tag: null,
                  most_cited_count: 0,
                }
              }
              emptyMessage="No data available for the facility."
              onViewHistory={
                hideExternalNavigation ? undefined : () => onNavigate("penalty")
              }
              infoTooltip={<CmsNursingHomePdcInfoTooltip meta={pdcMeta} />}
            />
          ) : null}
        </Box>

        <Box
          sx={{
            width: { xs: "100%", lg: 380 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: DASHBOARD_CARD_GAP,
          }}
        >
          <SectionCard sx={{ mb: 0, p: 0, overflow: "hidden" }}>
            <Accordion defaultExpanded disableGutters sx={dashboardAccordionSx}>
              <AccordionSummary
                {...dashboardAccordionSummaryRootProps}
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  ...dashboardAccordionSummarySx,
                  px: 3,
                  py: 1.5,
                  alignItems: "flex-start",
                  "& .MuiAccordionSummary-expandIconWrapper": { color: DASHBOARD.header, mt: 1 },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                  <DashboardSectionHeader
                    icon={<DescriptionOutlinedIcon fontSize="small" />}
                    title="Document expiry"
                    subtitle="Licenses, accreditations, certifications"
                    infoTooltip={<DocumentExpiryInfoTooltip />}
                  />
                  {!hideExternalNavigation ? (
                    <Box sx={{ display: "flex", gap: 1.5, mt: 0.75 }}>
                      <Box sx={{ width: 20, flexShrink: 0 }} />
                      <DashboardTextActionLink onClick={() => onNavigate("facilityProfile")}>
                        Manage →
                      </DashboardTextActionLink>
                    </Box>
                  ) : null}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                <Box
                  sx={{
                    backgroundColor: "#F3FDFF",
                    border: "1px solid #E5F5F8",
                    borderRadius: "8px",
                    px: 2,
                    py: 1.25,
                    "& > :last-child": { mb: 0 },
                    ...(data!.document_expiry.length > DOCUMENT_EXPIRY_SCROLL_THRESHOLD
                      ? { maxHeight: DOCUMENT_EXPIRY_LIST_MAX_HEIGHT, overflowY: "auto" }
                      : {}),
                  }}
                >
                  {data!.document_expiry.length === 0 ? (
                    <Typography sx={{ fontSize: 14, color: DASHBOARD.text }}>
                      Add license and certification dates in your facility profile.
                    </Typography>
                  ) : (
                    data!.document_expiry.map((doc, idx) => (
                      <Box
                        key={doc.document_name + doc.expiry_date}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "stretch",
                          py: 0.75,
                          ...(idx < data!.document_expiry.length - 1
                            ? { borderBottom: `1px solid ${DASHBOARD.border}` }
                            : {}),
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 14, color: DASHBOARD.header }}>
                            {doc.document_name}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: DASHBOARD.accent }}>
                            {doc.expiry_date}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            ...dashboardBadgeSx(doc.badge_tone),
                            borderRadius: "6px",
                            px: 1.5,
                            minWidth: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {doc.badge_label}
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </SectionCard>

          {REGULATION_IMPACT_SECTION_ENABLED ? (
            <SectionCard sx={{ mb: 0, p: 0, overflow: "hidden" }}>
              <Accordion disableGutters sx={dashboardAccordionSx}>
                <AccordionSummary
                  {...dashboardAccordionSummaryRootProps}
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ ...dashboardAccordionSummarySx, px: 3, py: 1.5 }}
                >
                  <DashboardSectionHeader
                    icon={<GavelOutlinedIcon fontSize="small" />}
                    title="Regulation impact"
                    subtitle="New regulatory changes and their impact on this facility"
                    onAction={() => onNavigate("discover")}
                    actionLabel="Discover →"
                    infoTooltip={<RegulationImpactInfoTooltip lookbackDays={regUpdates?.lookback_days ?? 30} />}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                  {data!.regulation_impact.length === 0 ? (
                    <Typography sx={{ fontSize: 14, color: DASHBOARD.text }}>
                      No new regulation changes in the last {regUpdates!.lookback_days} days
                    </Typography>
                  ) : (
                    data!.regulation_impact.map((row) => {
                      const navTag = row.reference_codes?.[0] ?? row.regulation_tag;
                      return (
                        <Box
                          key={`${row.regulation_tag}-${row.title}-${row.analysis_history_id ?? ""}`}
                          component="button"
                          type="button"
                          onClick={() =>
                            onNavigate("discover", {
                              category: "latest-updates",
                              somTag: navTag,
                            })
                          }
                          sx={{
                            ...dashboardListItemSx,
                            flexDirection: "column",
                            alignItems: "stretch",
                            width: "100%",
                            border: "none",
                            background: "none",
                            textAlign: "left",
                            cursor: "pointer",
                            fontFamily: "inherit",
                            "&:hover": { bgcolor: DASHBOARD.listItemBg },
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                            <Chip
                              label={row.regulation_tag}
                              size="small"
                              sx={{
                                bgcolor: DASHBOARD.surfaceMuted,
                                border: `1px solid ${DASHBOARD.borderSubtle}`,
                                color: DASHBOARD.header,
                              }}
                            />
                            <Chip label={row.impact_label} size="small" sx={dashboardImpactChipSx(row.impact_level)} />
                            {(row.reference_codes ?? [])
                              .filter((code) => code !== row.regulation_tag)
                              .map((code) => (
                                <Chip
                                  key={code}
                                  label={code}
                                  size="small"
                                  sx={{
                                    fontSize: 11,
                                    bgcolor: DASHBOARD.listItemBg,
                                    border: `1px solid ${DASHBOARD.borderSubtle}`,
                                    color: DASHBOARD.text,
                                  }}
                                />
                              ))}
                          </Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 14, color: DASHBOARD.header }}>
                            {row.title}
                          </Typography>
                          {row.source_document_name ? (
                            <Typography sx={{ fontSize: 12, color: DASHBOARD.textDetail, mt: 0.25 }}>
                              {row.source_document_name}
                            </Typography>
                          ) : null}
                          <Typography sx={{ fontSize: 13, color: DASHBOARD.text, mt: 0.25 }}>
                            {row.description}
                          </Typography>
                        </Box>
                      );
                    })
                  )}
                </AccordionDetails>
              </Accordion>
            </SectionCard>
          ) : null}

          <SectionCard sx={{ mb: 0, p: 0, overflow: "hidden" }}>
            <Accordion defaultExpanded disableGutters sx={dashboardAccordionSx}>
              <AccordionSummary
                {...dashboardAccordionSummaryRootProps}
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  ...dashboardAccordionSummarySx,
                  px: 3,
                  py: 1.5,
                  alignItems: "flex-start",
                  "& .MuiAccordionSummary-expandIconWrapper": { color: DASHBOARD.header, mt: 1 },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                  <DashboardSectionHeader
                    icon={<PersonOutlineIcon fontSize="small" />}
                    title="Facility profile"
                    infoTooltip={
                      <FacilityProfileCompletenessInfoTooltip
                        profileCompleteness={data!.profile_completeness}
                      />
                    }
                  />
                  {!hideExternalNavigation ? (
                    <Box sx={{ display: "flex", gap: 1.5, mt: 0.75 }}>
                      <Box sx={{ width: 20, flexShrink: 0 }} />
                      <DashboardTextActionLink onClick={() => onNavigate("facilityProfile")}>
                        Edit →
                      </DashboardTextActionLink>
                    </Box>
                  ) : null}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                <Box
                  sx={{
                    backgroundColor: "#F3FDFF",
                    border: "1px solid #E5F5F8",
                    borderRadius: "8px",
                    p: 2,
                    "& > :last-child": { mb: 0 },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: DASHBOARD.header }}>
                      Completeness
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: DASHBOARD.text }}>
                      {data!.profile_completeness.completed_count} / {data!.profile_completeness.total_count} fields
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={data!.profile_completeness.completeness_percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      mb: 1.5,
                      backgroundColor: DASHBOARD.borderSubtle,
                      "& .MuiLinearProgress-bar": {
                        background: "linear-gradient(94.5deg, #35D7E4 -2.59%, #0A829D 115.51%)",
                        borderRadius: 4,
                      },
                    }}
                  />
                  {data!.profile_completeness.completeness_percentage >= 100 ? (
                    <Typography sx={{ color: DASHBOARD.accent, fontSize: 13, fontWeight: 600 }}>
                      Profile complete
                    </Typography>
                  ) : (
                    <>
                      {data!.profile_completeness.missing_fields.length > 0 ? (
                        <Typography sx={{ fontSize: 13, color: DASHBOARD.text }}>
                          {data!.profile_completeness.missing_fields.join(", ")} missing
                        </Typography>
                      ) : null}
                      {(data!.profile_completeness.missing_optional_fields?.length ?? 0) >
                      0 ? (
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: DASHBOARD.text,
                            mt:
                              data!.profile_completeness.missing_fields.length > 0
                                ? 0.75
                                : 0,
                          }}
                        >
                          Optional still needed:{" "}
                          {data!.profile_completeness.missing_optional_fields!.join(", ")}
                        </Typography>
                      ) : null}
                    </>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </SectionCard>
        </Box>
      </Box>
      )}
    </Box>
  );
}

